import { useOrganization } from '@library/react-toolkit'
import { pipe } from 'fp-ts/lib/function'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { throwLeft } from '../../axios-extended'
import { Item } from '../../types'
import { useShowError, useShowSuccess } from '../Notifications'
import * as api from './api'
import { NewItem, Query } from './types'

export const useClearItems = () => {
  const org = useOrganization()
  const queryClient = useQueryClient()

  return () => queryClient.invalidateQueries([org?.name, 'items'])
}

export const useItems = (q: Query) => {
  const org = useOrganization()

  return useQuery(
    [org?.name, 'items', q],
    ({ signal }) => pipe(api.getItems(org?.name ?? '', q, signal), throwLeft)(),
    { enabled: !!org, keepPreviousData: true }
  )
}

export const useAddItem = () => {
  const showError = useShowError()
  const showSuccess = useShowSuccess()
  const org = useOrganization()
  const clearItems = useClearItems()

  return useMutation(
    (item: NewItem) => pipe(api.addItem(org?.name ?? '', item), throwLeft)(),
    {
      onSuccess: () => {
        clearItems()
        showSuccess('addItemSuccess')
      },
      onError: () => {
        showError('addItemError')
      },
    }
  )
}

export const useDeleteItem = () => {
  const showError = useShowError()
  const showSuccess = useShowSuccess()
  const org = useOrganization()
  const clearItems = useClearItems()

  return useMutation(
    [org?.name, 'delete-item'],
    (id: number) => pipe(api.deleteItem(org?.name ?? '', id), throwLeft)(),
    {
      onSuccess: () => {
        clearItems()
        showSuccess('deleteItemSuccess')
      },
      onError: () => {
        showError('deleteItemError')
      },
    }
  )
}

export const useUpdateItem = () => {
  const showError = useShowError()
  const showSuccess = useShowSuccess()
  const org = useOrganization()
  const clearItems = useClearItems()

  return useMutation(
    (item: Item) => pipe(api.editItem(org?.name ?? '', item), throwLeft)(),
    {
      onSuccess: () => {
        clearItems()
        showSuccess('editItemSuccess')
      },
      onError: () => {
        showError('editItemError')
      },
    }
  )
}
